import { AtAGlanceWidget } from '@/components/Widget/AtAGlanceWidget/AtAGlanceWidget';
import { ChatWidget } from '@/components/Widget/ChatWidget/ChatWidget';
import { ChosenYachtWidget } from '@/components/Widget/ChosenYachtWidget/ChosenYachtWidget';
import { CrewRosterWidget } from '@/components/Widget/CrewRosterWidget/CrewRosterWidget';
import { FinancialStatusWidget } from '@/components/Widget/FinancialStatusWidget/FinancialStatusWidget';
import { InvoicesToPayWidget } from '@/components/Widget/InvoicesToPayWidget/InvoicesToPayWidget';
import { LogsWidget } from '@/components/Widget/LogsWidget/LogsWidget';
import { MaintenanceTasksWidget } from '@/components/Widget/MaintenanceTasksWidget/MaintenanceTasksWidget';
import { NeedsAttentionWidget } from '@/components/Widget/NeedsAttentionWidget/NeedsAttentionWidget';
import { NextEventWidget } from '@/components/Widget/NextEventWidget/NextEventWidget';
import OurMemoriesWidget from '@/components/Widget/OurMemoriesWidget/OurMemoriesWidget';
import { ProjectsWidget } from '@/components/Widget/ProjectsWidget/ProjectsWidget';
import { SpendBarChartWidget } from '@/components/Widget/SpendBarChartWidget/SpendBarChartWidget';
import { SpendingByCategoryWidget } from '@/components/Widget/SpendingByCategoryWidget/SpendingByCategoryWidget';
import { WeatherWidget } from '@/components/Widget/WeatherWidget/WeatherWidget';
import { WhosOnBoardWidget } from '@/components/Widget/WhosOnBoardWidget/WhosOnBoardWidget';
import { type Permission, PERMISSIONS } from '@/permissions/permissions.catalog';
import type { AccessVerdict } from '@/permissions/plan-access';
import {
  BUDGET_RIGHTS,
  PROJECTS_RIGHTS,
  TASK_RIGHTS,
  TRANSACTION_RIGHTS,
} from '@/permissions/route-rights';
import { LOG_BOOKS } from '@pages/LogbookPage/logbookRoutes';

import type { WidgetDefinition, WidgetId, WidgetLayoutItem } from './dashboard.types';
import { UserRole } from './roles';

export const WIDGET_REGISTRY: Record<WidgetId, WidgetDefinition> = {
  at_a_glance: {
    id: 'at_a_glance',
    label: 'Vessel Statistics',
    category: 'vessel',
    component: AtAGlanceWidget,
    minColSpan: 4,
    maxColSpan: 4,
    defaultColSpan: 4,
    mobileSupported: true,
    allowedPermissions: [],
  },
  chosen_yacht: {
    id: 'chosen_yacht',
    label: 'My Yacht',
    category: 'vessel',
    component: ChosenYachtWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    lockedOnMobile: true,
    allowedPermissions: [],
    disallowedRoles: [UserRole.SERVICE_PROVIDER],
  },
  needs_attention: {
    id: 'needs_attention',
    label: 'Needs Attention',
    category: 'tasks',
    component: NeedsAttentionWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    /** Mirrors activity's `externalTasks` middleware, which is what `GET /tasks` clears. */
    allowedPermissions: [PERMISSIONS.activity.ExternalTasks, PERMISSIONS.activity.Tasks],
  },
  next_event: {
    id: 'next_event',
    label: 'Next Trip',
    category: 'events',
    component: NextEventWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: [],
  },
  weather: {
    id: 'weather',
    label: 'Sea Conditions',
    category: 'weather',
    component: WeatherWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: [],
  },
  whos_on_board: {
    id: 'whos_on_board',
    label: 'Who’s Aboard',
    category: 'crew',
    component: WhosOnBoardWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: [],
    disallowedRoles: [UserRole.SERVICE_PROVIDER],
  },
  budget: {
    id: 'budget',
    label: 'Budget',
    category: 'finances',
    component: SpendingByCategoryWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    /**
     * Budget rights, not `Transactions`, even though the figures are aggregated from
     * transactions: the widget titles itself "Budget" and every slice opens
     * `/manage/budget-details`. On a plan without budgets it was presenting itself as a
     * budget it could not show and linking somewhere the plan cannot open. The plain
     * spend breakdown it draws is still on the Manage hub, which is not budget-gated.
     */
    allowedPermissions: [PERMISSIONS.wallet.GetBudgets, PERMISSIONS.wallet.EditBudgets],
  },
  our_memories: {
    id: 'our_memories',
    label: 'Our Memories',
    category: 'media',
    component: OurMemoriesWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    /**
     * The vessel album is `GET /gallery`, which activity gates on `InternalGallery` alone —
     * the base `ExternalGallery` only opens the per-event albums, not the vessel's own.
     */
    allowedPermissions: [PERMISSIONS.activity.InternalGallery],
  },
  chat: {
    id: 'chat',
    label: 'Messages',
    category: 'chat',
    component: ChatWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: [PERMISSIONS.communication.RoomsBasic],
  },
  projects: {
    id: 'projects',
    label: 'Projects',
    category: 'events',
    component: ProjectsWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: [...PROJECTS_RIGHTS],
  },
  logs: {
    id: 'logs',
    label: 'Logs',
    category: 'vessel',
    component: LogsWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: LOG_BOOKS.map((book) => book.permission),
  },
  crew_roster: {
    id: 'crew_roster',
    label: 'Crew',
    category: 'crew',
    component: CrewRosterWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: [PERMISSIONS.users.CrewManagement],
  },
  invoices_to_pay: {
    id: 'invoices_to_pay',
    label: 'Invoices to Pay',
    category: 'finances',
    component: InvoicesToPayWidget,
    minColSpan: 2,
    maxColSpan: 2,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: [PERMISSIONS.wallet.Invoices],
  },
  financial_status: {
    id: 'financial_status',
    label: 'Financial Status',
    category: 'finances',
    component: FinancialStatusWidget,
    minColSpan: 2,
    maxColSpan: 2,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: [...BUDGET_RIGHTS],
  },
  spend_by_category: {
    id: 'spend_by_category',
    label: 'Spend by Category',
    category: 'finances',
    component: SpendBarChartWidget,
    minColSpan: 2,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: [...TRANSACTION_RIGHTS],
  },
  maintenance_tasks: {
    id: 'maintenance_tasks',
    label: 'Maintenance Tasks',
    category: 'tasks',
    component: MaintenanceTasksWidget,
    minColSpan: 1,
    maxColSpan: 4,
    defaultColSpan: 2,
    mobileSupported: true,
    allowedPermissions: [...TASK_RIGHTS],
  },
};

export const WIDGET_ORDER: WidgetId[] = [
  'at_a_glance',
  'chosen_yacht',
  'needs_attention',
  'next_event',
  'weather',
  'whos_on_board',
  'budget',
  'our_memories',
  'chat',
  'projects',
  'logs',
  'crew_roster',
  'invoices_to_pay',
  'financial_status',
  'spend_by_category',
  'maintenance_tasks',
];

export function getWidgetDef(id: WidgetId): WidgetDefinition | undefined {
  return WIDGET_REGISTRY[id];
}

export function getWidgetsForRights(
  rights: ReadonlySet<string>,
  userRoles: readonly UserRole[] = [],
): WidgetDefinition[] {
  return WIDGET_ORDER.map((id) => WIDGET_REGISTRY[id]).filter((w) => {
    const permitted =
      w.allowedPermissions.length === 0 || w.allowedPermissions.some((p) => rights.has(p));
    const roleAllowed = !w.disallowedRoles || !w.disallowedRoles.some((r) => userRoles.includes(r));
    return permitted && roleAllowed;
  });
}

/**
 * Whether a widget renders, renders as a plan placeholder, or is absent.
 *
 * `allowedPermissions` is any-of, so one granted right opens the widget and — when none is
 * granted — one right an upgrade would restore is enough to make the placeholder honest.
 * A widget its role is barred from is `hidden` whatever the plan says: no plan sells a
 * service provider the crew widget.
 */
export function widgetAccess(
  def: WidgetDefinition,
  userRoles: readonly UserRole[],
  anyVerdictFor: (permissions: readonly Permission[]) => AccessVerdict,
): AccessVerdict {
  if (def.disallowedRoles?.some((role) => userRoles.includes(role))) return 'hidden';
  if (def.allowedPermissions.length === 0) return 'open';

  return anyVerdictFor(def.allowedPermissions);
}

/**
 * The layout a dashboard should render: the saved one minus what this user cannot see, plus
 * the plan-locked widgets folded back in.
 *
 * Folding in matters. The settings page only ever writes widgets the plan carried, so a
 * locked widget is usually missing from the saved layout altogether — filtering alone would
 * never surface it, and the owner would see no sign the widget exists.
 */
export function withLockedWidgets(
  items: readonly WidgetLayoutItem[],
  accessFor: (id: WidgetId) => AccessVerdict,
): WidgetLayoutItem[] {
  const kept = items.filter((item) => accessFor(item.id) !== 'hidden');
  const present = new Set(kept.map((item) => item.id));
  const nextOrder = kept.reduce((max, item) => Math.max(max, item.order), -1) + 1;

  const locked = WIDGET_ORDER.filter((id) => !present.has(id) && accessFor(id) === 'locked').map(
    (id, index) => ({
      id,
      visible: true,
      order: nextOrder + index,
      col_span: WIDGET_REGISTRY[id].defaultColSpan,
    }),
  );

  return [...kept, ...locked];
}
